import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { FlexHorizontal, Spacer } from '@vestaboard/installables'
import React, { useState } from 'react'

import { Alert } from '@material-ui/lab'
import InfiniteScroll from 'react-infinite-scroller'
import { ListTab } from '../components/ListTab'
import { SearchInput } from '../../../ui/SearchInput'
import { TruncatedId } from '../../truncated-id'
import { isUUID } from '../../../utils'
import { useGetPersonQuery } from '../../../api/useGetPersonName'
import { useHistory } from 'react-router'
import { useListNotificationSubscribers } from '../hooks/useListNotificationSubscribers'

export const ListSubscribers = () => {
  const [terms, setTerms] = useState('' as string)
  const history = useHistory()
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const input = {
    cursor: null,
    limit: 25,
    terms
  }

  const { loading, error, data, fetchMore } = useListNotificationSubscribers(
    {
      input
    },
    !terms || isUUID(terms)
  )
  const { data: personData, loading: personLoading, error: personError } = useGetPersonQuery({
    variables: {
      personId: terms
    },
    skip: !isUUID(terms)
  })

  return (
    <Box>
      <FlexHorizontal spaceBetween>
        <Typography variant='h4'>Notification Subscribers</Typography>
        <FlexHorizontal>
          <SearchInput
            onSearch={term => {
              setTerms(term)
            }}
          />
        </FlexHorizontal>
      </FlexHorizontal>
      <Spacer size='extraLarge' />
      <ListTab activeTab='subscribers' />
      {(loading && !data?.listNotificationSubscribers.notificationSubscribers) || personLoading ? (
        <CircularProgress />
      ) : error || personError ? (
        <Alert severity='error'>There was an error loading the notification subscribers</Alert>
      ) : !data?.listNotificationSubscribers.notificationSubscribers.length && !personData?.person?.id ? (
        <Alert severity='info'>
          {!terms
            ? 'Search for a notification subscriber in the search field above'
            : 'There are no notification subscribers'}
        </Alert>
      ) : (
        <>
          {personData ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Unread Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow key={personData.person.id}>
                    <TableCell>
                      <Button
                        onClick={() => history.push(`/notifications/subscriber/${personData.person.id}`)}
                        variant='outlined'
                        color='primary'>
                        <TruncatedId value={personData.person.id} full />
                      </Button>
                    </TableCell>
                    <TableCell>
                      {`${personData.person.firstName || ''} ${personData.person.lastName || ''}`.trim()}
                    </TableCell>
                    <TableCell>--</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : data ? (
            <InfiniteScroll
              pageStart={0}
              loadMore={async () => {
                if (isLoadingMore) {
                  return
                }

                setIsLoadingMore(true)
                return await fetchMore({
                  updateQuery(previousQueryResult, options) {
                    const newSearch = options.fetchMoreResult?.listNotificationSubscribers.notificationSubscribers || []
                    const previousSearch = previousQueryResult.listNotificationSubscribers.notificationSubscribers || []

                    return {
                      listNotificationSubscribers: {
                        ...previousQueryResult.listNotificationSubscribers,
                        nextCursor: options.fetchMoreResult?.listNotificationSubscribers.nextCursor || null,
                        notificationSubscribers: [...previousSearch, ...newSearch]
                      }
                    }
                  },
                  variables: {
                    input: {
                      ...input,
                      cursor: data.listNotificationSubscribers.nextCursor || null
                    }
                  }
                }).then(res => {
                  setIsLoadingMore(false)
                  return res
                })
              }}
              hasMore={!!data?.listNotificationSubscribers?.nextCursor}
              loader={
                <Box>
                  <Spacer size='large' />
                  <CircularProgress />
                </Box>
              }>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Unread Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.listNotificationSubscribers.notificationSubscribers.map(subscriber => (
                      <TableRow key={subscriber.id}>
                        <TableCell>
                          <Button
                            onClick={() => history.push(`/notifications/subscriber/${subscriber.id}`)}
                            variant='outlined'
                            color='primary'>
                            <TruncatedId value={subscriber.id} full />
                          </Button>
                        </TableCell>
                        <TableCell>{subscriber.title}</TableCell>
                        <TableCell>{(subscriber.unreadNotificationsCount || 0).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </InfiniteScroll>
          ) : null}
        </>
      )}
    </Box>
  )
}
